import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getCognitiveParticipants, CognitiveParticipant } from '@services/airtable';

// ─── Card color by result ────────────────────────────────────────────────────
function getCardColor(card: string) {
  const lower = card.toLowerCase();
  if (lower.includes('amarilla')) return '#ffdf00';
  if (lower.includes('magenta')) return '#ea00d9';
  if (lower.includes('cian')) return '#00d2ff';
  if (lower.includes('naranja')) return '#ff6b35';
  return '#2ecc71';
}

// ─── Flip Card ────────────────────────────────────────────────────────────────
function FlipCard({ participant }: { participant: CognitiveParticipant }) {
  const [flipped, setFlipped] = useState(false);
  const color = getCardColor(participant.resultCard);

  return (
    <div
      className="relative mx-auto w-full max-w-md cursor-pointer select-none"
      style={{ perspective: '1600px', height: 580 }}
      onClick={() => setFlipped((f) => !f)}
    >
      <motion.div
        className="relative h-full w-full"
        style={{ transformStyle: 'preserve-3d' }}
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* ─── FRONT ─── */}
        <div
          className="absolute inset-0 rounded-3xl overflow-hidden"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            background: 'linear-gradient(160deg, rgba(20,20,30,0.95) 0%, rgba(8,8,14,0.98) 100%)',
            border: `1px solid ${color}55`,
            boxShadow: `0 0 40px -12px ${color}, inset 0 0 60px -30px ${color}`
          }}
        >
          {/* holographic sheen */}
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              background: `radial-gradient(circle at 30% 20%, ${color}30, transparent 45%), radial-gradient(circle at 80% 90%, ${color}22, transparent 40%)`
            }}
          />
          <div className="relative z-10 flex h-full flex-col items-center justify-between p-8 text-center">
            <div className="w-full">
              <span
                className="inline-block rounded-full px-3 py-1 text-[11px] font-semibold tracking-wide"
                style={{ backgroundColor: `${color}22`, color, border: `1px solid ${color}55` }}
              >
                {participant.account || 'Cognitive Augment'}
              </span>
            </div>

            <div className="flex flex-col items-center">
              <motion.div
                className="mb-5 grid h-28 w-28 place-items-center rounded-full text-5xl"
                style={{
                  background: `radial-gradient(circle at 50% 40%, ${color}40, transparent 70%)`,
                  border: `2px solid ${color}66`
                }}
                animate={{ boxShadow: [`0 0 20px -6px ${color}`, `0 0 45px -6px ${color}`, `0 0 20px -6px ${color}`] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
              >
                {participant.resultCard.match(/\p{Emoji}/u)?.[0] ?? '🧠'}
              </motion.div>
              <h3 className="text-3xl font-bold text-white">{participant.name}</h3>
              <p className="mt-1 text-sm text-gray-400">{participant.role}</p>
              <p className="mt-4 text-sm font-semibold" style={{ color }}>
                {participant.resultCard.replace(/\p{Emoji}/gu, '').trim()}
              </p>
            </div>

            {/* scores */}
            <div className="grid w-full grid-cols-4 gap-2">
              {[
                { l: 'Memoria', v: participant.memoryScore },
                { l: 'Atención', v: participant.attentionScore },
                { l: 'Sistema 2', v: participant.reasoningScore },
                { l: 'Base', v: participant.baseScore }
              ].map((s) => (
                <div key={s.l} className="rounded-xl border border-white/10 bg-white/[0.03] py-2">
                  <div className="text-lg font-bold text-white">{s.v}</div>
                  <div className="text-[10px] text-gray-500">{s.l}</div>
                </div>
              ))}
            </div>

            <p className="text-[11px] text-gray-600">Toca la tarjeta para ver el prompt →</p>
          </div>
        </div>

        {/* ─── BACK (Body Sandwich Layout) ─── */}
        <div
          className="absolute inset-0 rounded-3xl overflow-hidden"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
            background: 'linear-gradient(180deg, rgba(14,14,22,0.99) 0%, rgba(6,6,12,1) 100%)',
            border: `1px solid ${color}30`,
            boxShadow: `0 0 25px -15px ${color}`
          }}
        >
          <div className="relative z-10 flex h-full flex-col items-stretch p-5">
            {/* ── HEAD: Rol ── */}
            <div className="flex items-center gap-2.5 rounded-t-2xl border border-gray-700/60 bg-gray-800/40 px-4 py-3">
              <span className="text-lg">🎭</span>
              <div className="flex flex-col">
                <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Rol</span>
                <span className="text-[9px] text-gray-600">Define quién es la IA</span>
              </div>
              <div className="ml-auto h-3 w-3 rounded-full opacity-60" style={{ backgroundColor: color }} />
            </div>

            {/* ── SHOULDERS: Contexto ── */}
            <div className="flex items-center gap-2.5 border-x border-b border-gray-700/30 bg-gray-800/20 px-4 py-2.5">
              <span className="text-lg">📚</span>
              <div className="flex flex-col">
                <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Contexto</span>
                <span className="text-[9px] text-gray-600">Información de fondo relevante</span>
              </div>
              <div className="ml-auto h-2 w-2 rounded-full opacity-40" style={{ backgroundColor: color }} />
            </div>

            {/* ── CORE: The Instruction / Prompt Piece ── */}
            <div className="relative flex flex-1 items-center border-x px-4 py-4" style={{ borderColor: `${color}33` }}>
              {/* radial glow */}
              <div
                className="pointer-events-none absolute inset-0"
                style={{
                  background: `radial-gradient(ellipse at center, ${color}08, transparent 70%)`
                }}
              />
              <div
                className="relative w-full rounded-xl p-5 text-center"
                style={{
                  background: `linear-gradient(145deg, ${color}08, ${color}03)`,
                  border: `1px solid ${color}40`,
                  boxShadow: `0 0 15px -10px ${color}`
                }}
              >
                <span
                  className="absolute -top-2.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-0.5 text-[10px] font-bold uppercase tracking-wide"
                  style={{ backgroundColor: color, color: '#0a0a0f' }}
                >
                  🎯 Instrucción
                </span>
                {participant.promptPiece ? (
                  <p className="mt-2 text-[14px] font-medium leading-relaxed text-white">{participant.promptPiece}</p>
                ) : (
                  <p className="mt-2 text-sm italic text-gray-500">Aún no se ha generado la pieza del prompt.</p>
                )}
              </div>
            </div>

            {/* ── LEGS: Formato ── */}
            <div className="flex items-center gap-2.5 border-x border-t border-gray-700/30 bg-gray-800/20 px-4 py-2.5">
              <span className="text-lg">📋</span>
              <div className="flex flex-col">
                <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Formato</span>
                <span className="text-[9px] text-gray-600">Estructura de la salida</span>
              </div>
              <div className="ml-auto h-2 w-2 rounded-full opacity-40" style={{ backgroundColor: color }} />
            </div>

            {/* ── FEET: Restricciones ── */}
            <div className="flex items-center gap-2.5 rounded-b-2xl border border-gray-700/60 bg-gray-800/40 px-4 py-3">
              <span className="text-lg">⚠️</span>
              <div className="flex flex-col">
                <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">Restricciones</span>
                <span className="text-[9px] text-gray-600">Límites y reglas del prompt</span>
              </div>
              <div className="ml-auto h-3 w-3 rounded-full opacity-60" style={{ backgroundColor: color }} />
            </div>

            {/* hint */}
            <p className="mt-3 text-center text-[11px] text-gray-600">← Toca para volver</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────
export default function MyCardPage() {
  const [participants, setParticipants] = useState<CognitiveParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<CognitiveParticipant | null>(null);

  const fetchParticipants = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getCognitiveParticipants();
      setParticipants(data);
    } catch (err) {
      console.error(err);
      setError('Error al cargar los participantes. Revisa la consola.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchParticipants();
  }, []);

  return (
    <div className="min-h-screen px-4 py-12 dark:bg-black-100">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-10 text-center">
          <h2 className="text-4xl font-bold text-white md:text-5xl"> Mi Tarjeta</h2>
        </div>

        {/* Actions */}
        <div className="mb-4 flex justify-end">
          <button
            onClick={fetchParticipants}
            disabled={loading}
            className="rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-gray-300 transition-all hover:bg-white/10 hover:text-white"
          >
            {loading ? '⏳ Cargando...' : '🔄 Refrescar'}
          </button>
        </div>

        {error && (
          <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{error}</div>
        )}

        {!loading && participants.length === 0 && !error && (
          <div className="py-20 text-center text-gray-500">No hay estudiantes inscritos aún.</div>
        )}

        {/* Name list */}
        {participants.length > 0 && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {participants.map((p, idx) => {
              const color = getCardColor(p.resultCard);
              return (
                <motion.button
                  key={p.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                  onClick={() => setSelected(p)}
                  className="group flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-left transition-all hover:border-white/20 hover:bg-white/[0.06]"
                >
                  <div className="min-w-0">
                    <h3 className="truncate text-lg font-semibold text-white transition-colors group-hover:text-white">
                      {p.name}
                    </h3>
                    <p className="truncate text-xs text-gray-500">{p.role}</p>
                  </div>
                  <span
                    className="shrink-0 rounded-lg px-2.5 py-1 text-xs font-bold"
                    style={{ backgroundColor: `${color}20`, border: `1px solid ${color}60`, color }}
                  >
                    {p.resultCard.match(/\p{Emoji}/u)?.[0] ?? '🧠'}
                  </span>
                </motion.button>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* Flip card modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm"
            onClick={(e) => e.target === e.currentTarget && setSelected(null)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', stiffness: 260, damping: 24 }}
              className="w-full max-w-md"
            >
              <FlipCard participant={selected} />
              <div className="mt-4 text-center">
                <button
                  onClick={() => setSelected(null)}
                  className="rounded-xl border border-white/10 bg-white/5 px-5 py-2 text-sm text-gray-300 transition-all hover:bg-white/10 hover:text-white"
                >
                  ✕ Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
